import { useConfigStore } from "@/store/config";
import { isHumanVerificationDisabled } from "./turnstile.shared";

const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";
const EXECUTE_TIMEOUT = 30 * 1000;
const SCRIPT_POLL_INTERVAL = 50;
const SCRIPT_POLL_MAX = 10 * 1000;

let scriptPromise = null;
let widgetId = null;
let containerEl = null;
let renderedSiteKey = "";
let inflight = null;
let pending = null;

function getEnabledSiteKey() {
  const config = useConfigStore();
  const enabled =
    typeof config.getConfig === "function"
      ? config.getConfig("enable_human_verification")
      : config.enable_human_verification;
  if (isHumanVerificationDisabled(enabled)) {
    return "";
  }
  return config.turnstile_site_key;
}

function takePending() {
  const currentPending = pending;
  pending = null;

  if (currentPending?.timer) {
    clearTimeout(currentPending.timer);
  }

  return currentPending;
}

function loadScript() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("turnstile: window is undefined"));
  }

  if (window.turnstile) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[src^="${TURNSTILE_SCRIPT_URL}"]`,
    );

    if (existing) {
      const start = Date.now();
      const poll = () => {
        if (window.turnstile) {
          resolve();
          return;
        }

        if (Date.now() - start > SCRIPT_POLL_MAX) {
          scriptPromise = null;
          reject(new Error("turnstile: timeout waiting for existing script"));
          return;
        }

        setTimeout(poll, SCRIPT_POLL_INTERVAL);
      };

      poll();
      return;
    }

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.dataset.turnstile = "1";
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("turnstile: script load failed"));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

function ensureContainer() {
  if (containerEl) {
    return containerEl;
  }

  containerEl = document.createElement("div");
  containerEl.style.cssText =
    "position:fixed;top:-9999px;left:-9999px;width:0;height:0;overflow:hidden;pointer-events:none;";
  document.body.appendChild(containerEl);
  return containerEl;
}

function createWidget(siteKey) {
  if (widgetId != null && renderedSiteKey === siteKey) {
    return;
  }

  if (widgetId != null && window.turnstile) {
    try {
      window.turnstile.remove(widgetId);
    } catch (_) {}
    widgetId = null;
  }

  const container = ensureContainer();
  widgetId = window.turnstile.render(container, {
    sitekey: siteKey,
    size: "invisible",
    execution: "execute",
    retry: "never",
    callback: (token) => {
      const currentPending = takePending();
      if (currentPending) {
        currentPending.resolve(token);
      }
    },
    "error-callback": (error) => {
      const currentPending = takePending();
      if (currentPending) {
        currentPending.reject(new Error(`turnstile error: ${error || "unknown"}`));
      }
    },
    "timeout-callback": () => {
      const currentPending = takePending();
      if (currentPending) {
        currentPending.reject(new Error("turnstile: challenge timeout"));
      }
    },
    "expired-callback": () => {},
  });
  renderedSiteKey = siteKey;
}

async function ensureReady(siteKey) {
  await loadScript();

  if (!window.turnstile) {
    throw new Error("turnstile: global not available after script load");
  }

  createWidget(siteKey);

  if (widgetId == null) {
    throw new Error("turnstile: render returned null");
  }
}

export async function preloadTurnstile() {
  const siteKey = getEnabledSiteKey();

  if (!siteKey) {
    return;
  }

  if (widgetId != null && renderedSiteKey === siteKey) {
    return;
  }

  try {
    await ensureReady(siteKey);
  } catch (error) {
    console.warn("[turnstile] preload failed:", error);
  }
}

async function runChallenge(siteKey) {
  await ensureReady(siteKey);

  try {
    window.turnstile.reset(widgetId);
  } catch (_) {}

  return new Promise((resolve, reject) => {
    pending = {
      resolve,
      reject,
      timer: setTimeout(() => {
        const currentPending = takePending();
        if (currentPending) {
          currentPending.reject(new Error("turnstile: challenge timeout"));
        }
      }, EXECUTE_TIMEOUT),
    };

    try {
      window.turnstile.execute(widgetId);
    } catch (error) {
      takePending();
      reject(error);
    }
  });
}

export function getTurnstileToken() {
  const siteKey = getEnabledSiteKey();

  if (!siteKey) {
    return Promise.resolve("");
  }

  const previous = inflight ? inflight.catch(() => null) : Promise.resolve();
  const next = previous.then(() => runChallenge(siteKey));

  inflight = next;
  next.finally(() => {
    if (inflight === next) {
      inflight = null;
    }
  });

  return next;
}

export function resetTurnstile() {
  const currentPending = takePending();

  if (currentPending) {
    currentPending.reject(new Error("turnstile: reset by caller"));
  }

  inflight = null;

  if (widgetId != null && window.turnstile) {
    try {
      window.turnstile.reset(widgetId);
    } catch (_) {}
  }
}
